import { Link } from "react-router-dom";
import type { DashboardSmartException } from "@crewledger/shared/schemas";
import {
  AlertTriangle,
  Clock,
  FileWarning,
  Settings2,
  Tractor,
  Wrench,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/design";

interface SmartExceptionsProps {
  exceptions: DashboardSmartException[];
  loading: boolean;
}

const CATEGORY_ICONS: Record<DashboardSmartException["category"], typeof AlertTriangle> = {
  missing_entries: Clock,
  overtime_spike: AlertTriangle,
  equipment_missing_hours: Tractor,
  rule_conflicts: FileWarning,
  manual_overrides: Settings2,
};

const SEVERITY_STYLES: Record<DashboardSmartException["severity"], string> = {
  LOW: "border-slate-200 bg-slate-50",
  MEDIUM: "border-amber-200 bg-amber-50/50",
  HIGH: "border-orange-200 bg-orange-50/50",
  CRITICAL: "border-red-200 bg-red-50/50",
};

export function SmartExceptions({ exceptions, loading }: SmartExceptionsProps) {
  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-20 animate-pulse rounded-lg border bg-card-white" />
        ))}
      </div>
    );
  }

  if (exceptions.length === 0) {
    return (
      <EmptyState
        icon={Wrench}
        title="All clear"
        description="No exceptions need review right now. Pay confidence is high for this period."
        className="py-8"
      />
    );
  }

  return (
    <div className="space-y-3">
      {exceptions.map((group) => {
        const Icon = CATEGORY_ICONS[group.category];
        return (
          <div
            key={group.category}
            className={cn(
              "rounded-lg border p-4",
              SEVERITY_STYLES[group.severity],
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-navy-blueprint" />
                <div>
                  <h3 className="font-semibold text-navy-deep">{group.categoryLabel}</h3>
                  <p className="text-sm text-muted-foreground">
                    {group.count} item{group.count === 1 ? "" : "s"} needing attention
                  </p>
                </div>
              </div>
              <span className="rounded-full bg-card-white px-2 py-0.5 text-xs font-semibold text-navy-deep">
                {group.count}
              </span>
            </div>
            <ul className="mt-3 space-y-1.5">
              {group.items.map((item) => (
                <li key={item.id} className="text-sm text-navy-deep/80">
                  · {item.message}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <Link
        to="/audit-logs"
        className="inline-block text-sm font-medium text-orange-construction hover:underline"
      >
        View audit trail →
      </Link>
    </div>
  );
}
